import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SseService } from 'src/app/services/SseService';
import { SuscripcionesService } from 'src/app/services/suscripciones.service';
import { ContextoSalidaIbkr, SalidaOperacionBrokerService } from 'src/app/services/salida-operacion-broker.service';
import { BrokerAdapterFactory } from 'src/app/services/broker-adapter-factory.service';
import { OptimalWallet } from 'src/app/interface/OptimalWallet';
import { ConfirmDialogComponent } from '../../common/confirm-dialog/confirm-dialog.component';

interface ModeloSusc {
  id: number;
  nombre: string;
  bajista: boolean | null;
  /** id de subcartera real (idsubcarterascartera), '' si no se conoce. */
  idSubcartera: string;
}

interface FilaSalida {
  op: any;
  seleccionable: boolean;
  motivo: string;
  modelo: string;
  /** id del modelo resuelto (idmodelo de la operación o, si viene en 0, por sentido en la suscripción); 0 = desconocido. */
  idModelo: number;
  /** salidaFinDiaActivo del modelo de la operación; null = no se pudo determinar. */
  finDia: boolean | null;
  sl: boolean;
  tp: boolean;
  estado: 'pendiente' | 'ejecutando' | 'ok' | 'error';
  msg: string;
}

@Component({
  selector: 'app-salir-fin-dia-dialog',
  templateUrl: './salir-fin-dia-dialog.component.html',
  styleUrl: './salir-fin-dia-dialog.component.scss'
})
export class SalirFinDiaDialogComponent implements OnInit {
  // Pausa entre cierres para no saturar al broker (mismo criterio que cerrarPosicionYEsperar).
  private readonly PAUSA_MS = 500;

  cargando = false;
  ejecutando = false;
  terminado = false;
  errorMsg = '';
  filas: FilaSalida[] = [];
  selection = new SelectionModel<FilaSalida>(true, []);
  progreso = 0;
  displayedColumns = ['select', 'isooper', 'compania', 'modelo', 'findia', 'pendiente', 'sentido', 'cantidad', 'entrada', 'oco', 'estado'];

  constructor(
    public dialogRef: MatDialogRef<SalirFinDiaDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { optimalWallet: OptimalWallet, licencia: string, isDark: boolean, idcarterasuscrita: number, automatizado?: () => boolean },
    private sseService: SseService,
    private suscripcionesService: SuscripcionesService,
    private salidaService: SalidaOperacionBrokerService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private cookieService: CookieService,
    private brokerAdapterFactory: BrokerAdapterFactory
  ) { }

  get esMetaApi(): boolean {
    return this.data.optimalWallet?.tipoBroker === 'METAAPI';
  }

  get seleccionadasConOco(): number {
    return this.selection.selected.filter(f => f.sl || f.tp).length;
  }

  ngOnInit(): void {
    this.cargar();
  }

  cargar(): void {
    this.cargando = true;
    this.errorMsg = '';
    const idcartera = this.data.optimalWallet.idcartera;
    const licencia = this.data.licencia || this.cookieService.get('auth_token');
    forkJoin({
      ops: this.sseService.apiGetWithAuth(`operaciones/${idcartera}`, licencia) as Observable<any[]>,
      modelos: this.cargarModelos()
    }).subscribe({
      next: ({ ops, modelos }) => {
        const filas = this.construirFilas(ops || [], idcartera, modelos);
        const idsModelo = Array.from(new Set(filas.map(f => f.idModelo).filter(id => id > 0)));
        this.cargarFlagsFinDia(idsModelo).subscribe(flags => {
          filas.forEach(f => f.finDia = flags.get(f.idModelo) ?? null);
          this.filas = filas;
          this.selection.clear();
          // Por defecto las operaciones cuyo modelo tiene salida de fin de día activa y las que tienen la
          // salida del modelo pendiente de enviar (el modelo ya quiere salir).
          this.filas.filter(f => f.seleccionable && (f.finDia === true || !!f.op.salidaPendiente)).forEach(f => this.selection.select(f));
          this.cargando = false;
        });
      },
      error: () => {
        this.cargando = false;
        this.errorMsg = 'Error al consultar las operaciones de la cartera.';
      }
    });
  }

  // Modelos de la suscripción de la cartera (idsubcarteras/subcarteras/bajista son CSV alineados;
  // sin subcarteras se usa idmodelo/nombre). Mismo criterio que sincronizar-comparativa.
  private cargarModelos(): Observable<ModeloSusc[]> {
    return (this.suscripcionesService.listarSuscripcionesConfirmar('') as Observable<any[]>).pipe(
      map((value: any[]) => {
        const s = (value || []).find((x: any) => x.idcartera === this.data.idcarterasuscrita);
        const modelos: ModeloSusc[] = [];
        if (!s) return modelos;
        if (s.idsubcarteras && s.subcarteras) {
          const ids = String(s.idsubcarteras).split(',');
          const noms = String(s.subcarteras).split(',');
          const bajs = s.bajista ? String(s.bajista).split(',') : [];
          const subIds = s.idsubcarterascartera ? String(s.idsubcarterascartera).split(',') : [];
          ids.forEach((id, i) => {
            if (noms[i] === undefined) return;
            modelos.push({ id: Number(id.trim()), nombre: noms[i].trim(), bajista: bajs[i] === undefined ? null : bajs[i].trim() === 'true', idSubcartera: (subIds[i] ?? '').trim() });
          });
        } else if (s.idmodelo) {
          modelos.push({ id: Number(s.idmodelo), nombre: s.nombre ?? String(s.idmodelo), bajista: s.bajista === undefined ? null : String(s.bajista).trim() === 'true', idSubcartera: s.idsubcarterascartera ? String(s.idsubcarterascartera).trim() : String(this.data.idcarterasuscrita) });
        }
        return modelos;
      }),
      catchError(() => of([] as ModeloSusc[]))
    );
  }

  // idmodelo en tmp_operacion puede venir en 0 en operaciones antiguas (operacionesSave no lo guardaba).
  // Orden de resolución: idmodelo de la operación → subcarteraid (de tbl_orden) contra las subcarteras
  // de la suscripción → por sentido (bajista) solo si exactamente un modelo coincide.
  private resolverModelo(op: any, modelos: ModeloSusc[]): ModeloSusc | null {
    const directo = Number(op.idmodelo) > 0 ? modelos.find(m => m.id === Number(op.idmodelo)) : undefined;
    if (directo) return directo;
    const sub = Number(op.subcarteraid) > 0 ? String(op.subcarteraid) : '';
    if (sub) {
      const porSub = modelos.find(m => m.idSubcartera === sub);
      if (porSub) return porSub;
    }
    if (Number(op.idmodelo) > 0) return null;
    const porSentido = modelos.filter(m => m.bajista === !!op.bajista);
    return porSentido.length === 1 ? porSentido[0] : null;
  }

  private cargarFlagsFinDia(idsModelo: number[]): Observable<Map<number, boolean | null>> {
    if (idsModelo.length === 0) return of(new Map());
    const llamadas = idsModelo.map(id =>
      (this.sseService.apiGetNew('parametrosmodelo?idmodelo=' + id) as Observable<any>).pipe(
        map((d: any) => {
          try { return !!JSON.parse(d.condicionln).salidaFinDiaActivo; } catch { return null; }
        }),
        catchError(() => of(null))
      )
    );
    return forkJoin(llamadas).pipe(
      map(res => new Map(idsModelo.map((id, i) => [id, res[i]] as [number, boolean | null])))
    );
  }

  get conFinDiaActivo(): number {
    return this.filas.filter(f => f.finDia === true).length;
  }

  get conSalidaPendiente(): number {
    return this.filas.filter(f => !!f.op.salidaPendiente).length;
  }

  // Abierta = entrada ejecutada sin salida enviada del mismo isooper y sentido; la señal de salida no
  // enviada queda en op.salidaPendiente (ver salidaService.operacionesAbiertas).
  private construirFilas(ops: any[], idcartera: number, modelos: ModeloSusc[]): FilaSalida[] {
    return this.salidaService.operacionesAbiertas(ops, idcartera)
      .map(op => {
        let motivo = '';
        if (!(Number(op.cantidadacciones) > 0)) motivo = 'Cantidad inválida';
        else if (!this.esMetaApi && !(Number(op.conid) > 0)) motivo = 'Sin conid del broker';
        const m = this.resolverModelo(op, modelos);
        return {
          op,
          seleccionable: !motivo,
          motivo,
          modelo: op.cartera || (m ? m.nombre : (Number(op.idmodelo) > 0 ? 'Modelo ' + op.idmodelo : 'Sin modelo')),
          idModelo: m ? m.id : Number(op.idmodelo) || 0,
          finDia: null as boolean | null,
          sl: this.salidaService.tieneSl(op),
          tp: this.salidaService.tieneTp(op),
          estado: 'pendiente' as const,
          msg: ''
        };
      });
  }

  toggleFila(fila: FilaSalida): void {
    if (!fila.seleccionable || this.ejecutando) return;
    this.selection.toggle(fila);
  }

  todasSeleccionadas(): boolean {
    const sel = this.filas.filter(f => f.seleccionable);
    return sel.length > 0 && sel.every(f => this.selection.isSelected(f));
  }

  toggleAll(): void {
    if (this.ejecutando) return;
    const sel = this.filas.filter(f => f.seleccionable);
    if (this.todasSeleccionadas()) sel.forEach(f => this.selection.deselect(f));
    else sel.forEach(f => this.selection.select(f));
  }

  sentido(f: FilaSalida): string {
    return f.op.bajista ? '↓ Bajista' : '↑ Alcista';
  }

  /** MetaTrader con Algo Trading desactivado: aviso y no se inicia el flujo. */
  confirmar(): void {
    // La automatización pudo activarse con el diálogo abierto: el modelo haría también la salida
    if (this.data.automatizado?.()) {
      this.toastr.warning('Detenga la automatización antes de salir a fin de día', 'Salir fin de día');
      return;
    }
    this.brokerAdapterFactory.puedeOperar(this.data.optimalWallet?.tipoBroker).subscribe(ok => { if (ok) this.confirmarConfirmado(); });
  }

  private confirmarConfirmado(): void {
    const seleccionadas = this.selection.selected;
    if (seleccionadas.length === 0) {
      this.toastr.warning('Seleccione al menos una operación', 'Salir fin de día');
      return;
    }
    const conOco = this.seleccionadasConOco;
    const aviso = conOco > 0 ? ` Se cancelarán las órdenes OCO (SL/TP) de ${conOco} operación(es).` : '';
    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '380px', height: '250px', panelClass: this.data.isDark ? 'dark-theme' : 'light-theme',
      data: { msj: `¿Cerrar en el broker ${seleccionadas.length} operación(es)?${aviso}`, icon: 'warning' }
    });
    ref.afterClosed().subscribe((ok: boolean) => {
      if (ok) this.ejecutar(seleccionadas);
    });
  }

  private async ejecutar(seleccionadas: FilaSalida[]): Promise<void> {
    this.ejecutando = true;
    this.terminado = false;
    this.progreso = 0;
    const licencia = this.data.licencia || this.cookieService.get('auth_token');
    // IBKR: el neto esperado de cada conid incluye todas las abiertas, no solo las seleccionadas
    const ctx: ContextoSalidaIbkr = { abiertas: this.filas.map(f => f.op), libros: new Map() };

    for (const fila of seleccionadas) {
      // La automatización pudo activarse durante la tanda: el modelo haría también la salida
      if (this.data.automatizado?.()) {
        this.toastr.warning('Automatización activada: se detiene la salida a fin de día', 'Salir fin de día');
        break;
      }
      fila.estado = 'ejecutando';
      fila.msg = '';
      const res = await new Promise<{ ok: boolean; msg: string }>(resolve =>
        this.salidaService.cerrarOperacion(fila.op, this.data.optimalWallet, licencia, ctx, 'SALIDA_FIN_DIA').subscribe({
          next: r => resolve(r),
          error: e => resolve({ ok: false, msg: e?.message ?? 'Error desconocido' })
        })
      );
      fila.estado = res.ok ? 'ok' : 'error';
      fila.msg = res.msg;
      this.progreso++;
      await new Promise(r => setTimeout(r, this.PAUSA_MS));
    }

    this.ejecutando = false;
    this.terminado = true;
    const cerradas = seleccionadas.filter(f => f.estado === 'ok').length;
    if (cerradas === seleccionadas.length) {
      this.toastr.success(`${cerradas} operación(es) cerradas`, 'Salir fin de día');
    } else {
      this.toastr.warning(`${cerradas} de ${seleccionadas.length} cerradas`, 'Salir fin de día');
    }
  }

  cerrar(): void {
    this.dialogRef.close(this.terminado);
  }
}
